import { useMemo, useState } from 'react';
import './accounting.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { ExportMenu } from './ExportMenu';
import { useNotify } from '../../components/notifications';
import { useConfirm, useReasonGate } from '../../components/confirm';
import { runWithAuditReason } from '../../api/client';
import { useFetchMs } from '../../hooks/useFetchMs';
import {
  usePaymentTermsSetup, usePaymentTerms, useSetCustomerPaymentTerm, useSavePaymentTerm, errMessage,
  type PaymentTermsSetupRow,
} from './accounting';

interface Props { onHome?: () => void; }

/**
 * Settings ▸ Accounting ▸ Payment Terms Setup (top-level Accounting node — the
 * per-client grid; SEPARATE from Utility ▸ Accounting which holds the lookup).
 * Mirrors the legacy ALPACA `Customer_ListView_PaymentTermsSetup`: a grid of
 * clients where Client / ClientCode / Address / Phone are read-only and only the
 * Terms column is editable — an inline dropdown bound to the PaymentTerms lookup
 * (`Customer.Terms` FK). No New / Delete (assignment only), exactly like ALPACA.
 */
export function PaymentTermsSetupPage({ onHome }: Props) {
  const notify = useNotify();
  const { alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = usePaymentTermsSetup();
  const { data: options = [] } = usePaymentTerms();           // day-value lookup → dropdown
  const loadMs = useFetchMs(isFetching);
  const setTerm = useSetCustomerPaymentTerm();
  const saveTerm = useSavePaymentTerm();
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  // Inline "New payment term" popup (mirrors the legacy lookup's + button →
  // the small "Payment Terms" dialog with a numeric Terms field).
  const [showNewTerm, setShowNewTerm] = useState(false);
  const [newTermValue, setNewTermValue] = useState('');
  const [newTermError, setNewTermError] = useState('');
  // Inline row-edit (mirrors the legacy pencil → edit-row flow): one row at a
  // time, and only the Terms dropdown is editable — Client / ClientCode /
  // Address / Phone stay read-only.  `draftTermsId` holds the pending pick
  // until Save commits it via the existing per-client endpoint.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTermsId, setDraftTermsId] = useState('');
  const [draftClientCode, setDraftClientCode] = useState('');
  // Leading selection checkbox column (parity with the legacy grid's first column).
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  const startEdit = (row: PaymentTermsSetupRow) => {
    setEditingId(row.id);
    setDraftTermsId(row.termsId ?? '');
    setDraftClientCode(row.clientCode ?? '');
  };
  const cancelEdit = () => { setEditingId(null); setDraftTermsId(''); setDraftClientCode(''); };
  const saveEdit = async (row: PaymentTermsSetupRow) => {
    // Reason-for-change gate (standing rule 11) — editing an existing client's
    // payment terms; prompts only when ReasonRequired is ON. null = cancelled.
    const reason = await gateReason('Payment Terms Setup', 'edit');
    if (reason === null) return;
    setSavingId(row.id);
    try {
      await runWithAuditReason(reason, () => setTerm.mutateAsync({
        customerId: row.id, termsId: draftTermsId || null, clientCode: draftClientCode.trim() || null,
      }));
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      setEditingId(null);
      setDraftTermsId('');
      setDraftClientCode('');
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSavingId(null); }
  };

  // Erase (the legacy lookup's clear button): set the row's Terms to none.
  const eraseTerm = () => setDraftTermsId('');

  // New (the legacy lookup's + button → "Payment Terms" popup): create a new
  // PaymentTerms day-value, then auto-select it as the current draft.
  const openNewTerm = () => { setNewTermValue(''); setNewTermError(''); setShowNewTerm(true); };
  const createNewTerm = async () => {
    const n = Number(newTermValue);
    if (!newTermValue.trim() || !Number.isInteger(n) || n <= 0) {
      setNewTermError('Enter a whole number of days greater than 0.');
      return;
    }
    try {
      const res = await saveTerm.mutateAsync({ terms: n });
      const newId = (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
      notify.success('Payment term added.');
      setShowNewTerm(false);
      if (newId) setDraftTermsId(newId);
    } catch (e) { notify.error(errMessage(e)); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.client, r.clientCode, r.address, r.phone, r.termDays != null ? `${r.termDays} days` : '']
        .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  // LIMS (XAF / SQL Server) sorts these string columns LEXICALLY — plain
  // culture string compare (NOT numeric-aware) with blanks first — unlike the
  // grid's default numeric-aware comparator. Reproduce that exactly so the sort
  // order matches the legacy grid (e.g. "12630…" before "1770…").
  const textCmp = (get: (r: PaymentTermsSetupRow) => string | null) =>
    (a: PaymentTermsSetupRow, b: PaymentTermsSetupRow) => {
      const av = (get(a) ?? '').trim(), bv = (get(b) ?? '').trim();
      if (av === '' && bv === '') return 0;
      if (av === '') return -1;          // blanks first (asc), matching SQL NULLs-first
      if (bv === '') return 1;
      return av.localeCompare(bv, undefined, { sensitivity: 'base' });
    };

  const columns: AppColumn<PaymentTermsSetupRow>[] = [
    {
      // Per-row edit column (the legacy pencil): pencil → edit mode; in edit
      // mode it becomes Save (disk) + Cancel (undo), exactly like ALPACA.
      field: 'rowedit', header: '', sortable: false, filterable: false, width: 56, align: 'center',
      render: (r) =>
        editingId === r.id ? (
          <span className="pts-rowedit" onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={savingId === r.id}
              onClick={() => void saveEdit(r)}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={savingId === r.id}
              onClick={cancelEdit}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Edit"
            onClick={(e) => { e.stopPropagation(); startEdit(r); }}>
            <i className="ti ti-edit" />
          </button>
        ),
    },
    // Client + Address have NO fixed width so they absorb the grid's leftover
    // space — this keeps the leading edit/checkbox columns (and ClientCode /
    // Phone / Terms) tight instead of letting them stretch to fill the row.
    // Only Address is click-sortable (the default sort); Client / ClientCode /
    // Phone / Terms keep their filter but show no sort (⇅) arrow.
    { field: 'client',     header: 'Client',     minWidth: 220, sortable: false, text: (r) => r.client ?? '' },
    {
      // ClientCode: read-only text until this row is in edit mode, then an inline
      // text input (legacy parity — editable in the row editor).
      field: 'clientCode', header: 'ClientCode', width: 120, minWidth: 100, sortable: false,
      text: (r) => r.clientCode ?? '',
      render: (r) =>
        editingId === r.id ? (
          <input className="pts-input" value={draftClientCode} disabled={savingId === r.id}
            onClick={(e) => e.stopPropagation()} onChange={(e) => setDraftClientCode(e.target.value)} />
        ) : (
          <span>{r.clientCode ?? ''}</span>
        ),
    },
    { field: 'address',    header: 'Address',     minWidth: 180, text: (r) => r.address ?? '', compare: textCmp((r) => r.address) },
    { field: 'phone',      header: 'Phone',       width: 150, minWidth: 120, sortable: false, text: (r) => r.phone ?? '' },
    {
      // Terms: read-only "{n} days" until this row is in edit mode, then an
      // inline dropdown bound to the PaymentTerms lookup (Customer.Terms FK).
      // Filterable but no sort (⇅) arrow.
      field: 'terms', header: 'Terms', width: 200, minWidth: 190, sortable: false,
      text: (r) => (r.termDays != null ? `${r.termDays} days` : ''),
      render: (r) =>
        editingId === r.id ? (
          <span className="pts-terms-edit" onClick={(e) => e.stopPropagation()}>
            <select className="pts-select" value={draftTermsId} disabled={savingId === r.id}
              onChange={(e) => setDraftTermsId(e.target.value)}>
              <option value="">N/A</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.terms} days</option>)}
            </select>
            <button className="pts-mini-btn" title="New payment term" disabled={savingId === r.id}
              onClick={openNewTerm}><i className="ti ti-plus" /></button>
            <button className="pts-mini-btn clr" title="Clear"
              disabled={savingId === r.id || !draftTermsId} onClick={eraseTerm}><i className="ti ti-eraser" /></button>
          </span>
        ) : (
          <span>{r.termDays != null ? `${r.termDays} days` : ''}</span>
        ),
    },
  ];

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" />
          <span>Accounting</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Payment Terms Setup</span>
        </div>
        {/* BO UI Standard v2: shared FormTitle heading (nav icon tile + title +
            the SINGLE top load-time chip + audit chip) ON the grey page, then
            the "N items" subtitle — never inside the grid card. */}
        <FormTitle pageId="acc-paymentterms-setup" loadMs={loadMs ?? null} loading={isFetching}
          auditFormName="Payment Terms Setup" />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>

      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row below the title (BO UI
            Standard §6). No New/Edit/Delete here: assignment-only form, the
            per-row pencil owns editing (ALPACA parity). Export rides the shared
            spacer to the far right. */}
        <AppToolbar
          onRefresh={() => void refetch()}
          refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="PaymentTermsSetup"
                headers={['Client', 'ClientCode', 'Address', 'Phone', 'Terms']}
                rows={visible.map((r) => [
                  r.client ?? '', r.clientCode ?? '', r.address ?? '', r.phone ?? '',
                  r.termDays != null ? `${r.termDays} days` : '',
                ])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (BO UI Standard) — not in the toolbar. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card">
          <AppDataGrid<PaymentTermsSetupRow>
            rows={visible}
            columns={columns}
            getRowId={(r) => r.id}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            selectedRowId={editingId}
            initialSort={{ key: 'address', dir: 'asc' }}
            loading={isFetching}
            emptyText="No clients found."
          />
        </div>
      </div>

      {options.length === 0 && (
        <div className="acc-hint">
          No payment-term values exist yet — add them under <strong>Settings ▸ Utility ▸ Accounting ▸
          Payment Terms</strong> (e.g. 30, 60, 90 days) so they appear in the Terms dropdown here, or
          use the <i className="ti ti-plus" /> button in the Terms editor.
        </div>
      )}

      {/* "New payment term" popup — mirrors the legacy lookup's + button → the
          small "Payment Terms" dialog (numeric Terms field, OK / Cancel). */}
      {showNewTerm && (
        <div className="acc-overlay" onMouseDown={() => setShowNewTerm(false)}>
          <div className="acc-dialog" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr">
              <span>Payment Terms</span>
              <i className="ti ti-x acc-dialog-x" onClick={() => setShowNewTerm(false)} />
            </div>
            <div className="acc-dialog-body">
              <div className="acc-field">
                <label className="acc-label">Terms (days)<span className="req">*</span></label>
                <input className="acc-input" type="number" min={1} step={1} autoFocus value={newTermValue}
                  onChange={(e) => { setNewTermValue(e.target.value); if (newTermError) setNewTermError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') void createNewTerm(); }} />
                {newTermError && <div className="acc-err">{newTermError}</div>}
              </div>
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void createNewTerm()} disabled={saveTerm.isPending}>
                <span className="tb-i">💾</span> {saveTerm.isPending ? 'Saving…' : 'OK'}
              </button>
              <button className="ca-btn" onClick={() => setShowNewTerm(false)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
